import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateNps, useUpdateNps } from '~/api/nps';
import { useUser } from '~/api/user';
import { Button } from '~/components';
import { Field, Textarea } from '~/components/Form';
import { darkColorTheme } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';
import { isNotBlank } from '~/utils/string';

import {
  activeStyle,
  bodyStyle,
  buttonStyle,
  closeButtonStyle,
  feedbackFormStyle,
  feedbackReceivedStyle,
  headerStyle,
  npsStyle,
  npsWrapperStyle,
  scoreListStyle,
  stepsEyebrowStyle,
  stepsStyle,
  stepsTitleStyle,
  submitFeedbackButtonStyle,
} from './Nps.css';

type ScoreRowProps = {
  onClick: (score: number) => void;
  selectedScore?: number;
};

export const Nps = () => {
  const { refetch: refetchUser } = useUser();
  const createNpsMutation = useCreateNps();
  const updateNpsMutation = useUpdateNps();

  const [currentStep, setCurrentStep] = useState<'score' | 'feedbackReceived'>('score');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);

  const isPending = createNpsMutation.isPending || updateNpsMutation.isPending;

  const { t } = useTranslation(['settings', 'common', 'validation']);

  const { handleSubmit, register } = useForm<{ feedback: string }>({
    defaultValues: {
      feedback: '',
    },
  });

  const closeNps = () => {
    createNpsMutation.mutate({ closed: true });
  };

  const saveFeedback = handleSubmit((data) => {
    if (!selectedScore) return;

    createNpsMutation.mutate(
      { score: selectedScore, closed: false },
      {
        onSuccess: ({ id }) => {
          updateNpsMutation.mutate(
            { id, reason: data.feedback },
            {
              onSuccess: () => {
                setCurrentStep('feedbackReceived');
                // Refetch the user so that `user.showNps` comes back as `false`
                setTimeout(() => refetchUser(), 5_000);
              },
            },
          );
        },
      },
    );
  });

  return (
    <aside className={clsx(darkColorTheme, npsWrapperStyle)}>
      <div className={npsStyle}>
        <Button
          aria-label={t('common:actions.close')}
          className={closeButtonStyle}
          disabled={isPending}
          icon="CloseSmall"
          onClick={closeNps}
        />

        <div className={stepsStyle}>
          {currentStep === 'score' && (
            <>
              <div className={headerStyle}>
                <p className={stepsEyebrowStyle}>{t('settings:nps.eyebrow')}</p>

                <p className={stepsTitleStyle}>{t(`settings:nps.titleStepOne`)}</p>
              </div>

              {/* Step 1: Form to fill in score */}
              <div className={bodyStyle}>
                <ScoreRow onClick={(score) => setSelectedScore(score)} selectedScore={selectedScore ?? undefined} />

                {selectedScore && (
                  <form className={feedbackFormStyle} onSubmit={saveFeedback}>
                    <Field.Root>
                      <Field.Label srOnly>{t('settings:nps.placeholderTextarea')}</Field.Label>
                      <Textarea
                        placeholder={t('settings:nps.placeholderTextarea')}
                        {...register('feedback', {
                          required: true,
                          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                        })}
                        aria-label={t('settings:nps.ariaLabelTextarea')}
                        rows={2}
                      />
                    </Field.Root>

                    <Button className={submitFeedbackButtonStyle} loading={isPending} type="submit">
                      {t('settings:nps.sendFeedback')}
                    </Button>
                  </form>
                )}
              </div>
            </>
          )}

          {/* Step 2: Thank you for your feedback */}
          {currentStep === 'feedbackReceived' && <p className={feedbackReceivedStyle}>{t('settings:nps.titleStepThree')}</p>}
        </div>
      </div>
    </aside>
  );
};

const ScoreRow = ({ onClick, selectedScore }: ScoreRowProps) => {
  return (
    <ul className={scoreListStyle}>
      {[...Array(10)].map((_, i) => {
        const score = i + 1;

        const active = score === selectedScore;

        return (
          <li key={i}>
            <Button className={clsx(buttonStyle, active && activeStyle)} emphasis="secondary" onClick={() => onClick(score)}>
              <data value={score}>{score}</data>
            </Button>
          </li>
        );
      })}
    </ul>
  );
};
